import { Vue, Component, Prop, Model, Watch } from 'vue-property-decorator'
import * as T from '@/types'
import { CreateElement } from 'vue'

import { notNull } from '@/utils'

import _capitalize from 'lodash/capitalize'

import {
    UiIcon,
    UiDropdown,
    UiMultiSelect,
} from '@hubsolv/hubsolv-ui'

type InputEntityValue<Entity> = Entity | T.EntityId | Entity[] | T.EntityId[] | null

// @ts-ignore
@Component
export abstract class AbstractInputEntityMixin<Entity extends T.Entity> extends Vue {

    ////////////////////////////////////////////////////////////////
    // API
    ////////////////////////////////////////////////////////////////

    @Model('input', { default: null }) value!: InputEntityValue<Entity>

    @Prop({ type: Boolean, default: false }) multiple!: boolean

    @Prop({ type: Boolean, default: false }) idOnly!: boolean
    @Prop({ type: Function, default: () => true }) shouldShowEntity!: (entity: Entity) => boolean

    @Prop({ type: Boolean, default: false }) disabled!: boolean
    @Prop({ type: Boolean, default: false }) readOnly!: boolean
    @Prop({ type: String, default: null }) tooltip!: string

    @Prop({type: Boolean, default: false}) highlighted!: Boolean

    ////////////////////////////////////////////////////////////////
    // data
    ////////////////////////////////////////////////////////////////

    valueToBind: Entity | Entity[] | null = null

    ////////////////////////////////////////////////////////////////
    // computed
    ////////////////////////////////////////////////////////////////

    get valueAsArray (): (Entity | T.EntityId)[] {
        if (Array.isArray(this.value)) return this.value
        if (this.value === null) return []
        return [ this.value ]
    }

    get label () {
        return _capitalize(
            !this.multiple ?
                this.singularDisplayName :
                this.pluralDisplayName,
        )
    }

    abstract get singularDisplayName (): string
    abstract get pluralDisplayName (): string

    get entities (): Entity[] { return [] }

    get options () {
        return this.entities.filter(this.shouldShowEntity)
    }

    abstract get scopedSlots (): T.Dictionary<(scope: any) => any>

    get scopedSlotsToUse (): T.Dictionary<(scope: any) => any> {
        const defaultScopedSlots: this['scopedSlotsToUse'] = {
            'right': ({ option }: { option: Entity }) => (
                this.isOptionArchived(option) ?
                <UiIcon
                    icon={this.deactivatable ? 'cancel' : 'package-down'}
                    small={true}
                    danger={true}
                    v-tooltip={`This ${this.singularDisplayName} has been ${this.deactivatable ? 'deactivated' : 'archived'}.`}
                /> :
                undefined
            ),
        }

        return {
            ...defaultScopedSlots,
            ...this.scopedSlots,
        }
    }

    get filterable () { return true }
    get editable () { return false }
    get creatable () { return false }
    get deactivatable () { return false }

    get async () { return false }

    ////////////////////////////////////////////////////////////////
    // watchers
    ////////////////////////////////////////////////////////////////

    @Watch('value', { immediate: true })
    onChangeValue () {
        this.updateValueToBind()
    }
    
    ////////////////////////////////////////////////////////////////
    // methods
    ////////////////////////////////////////////////////////////////

    async updateValueToBind () {
        const valueAsEntityArray: Entity[] = (await Promise.all(
            this.valueAsArray.map(async (element) => {
                if (typeof element === 'string') return await this.getEntityById(element) || null
                else return element
            }),
        )).filter(notNull)

        if (this.multiple) {
            this.valueToBind = valueAsEntityArray
        }
        else {
            this.valueToBind = valueAsEntityArray.length > 0 ? valueAsEntityArray[0] : null
        }
    }

    abstract getOptionValue (entity: Entity): any
    abstract getOptionFilterKey (entity: Entity): any
    getOptionTitle (entity: Entity): string | null { return null }
    getOptionColor (entity: Entity): T.Color | null { return null }
    isOptionArchived (entity: Entity): boolean { return false }

    async getEntityById (id: T.EntityId) {
        return Promise.resolve(
            this.entities.find((entity) => {
                return this.getOptionValue(entity) === id
            }),
        )
    }

    ////////////////////////////////////////////////////////////////
    // event handlers
    ////////////////////////////////////////////////////////////////

    onSelectOne (entity: Entity) {
        if (this.idOnly) {
            this.$emit('input', this.getOptionValue(entity))
        }
        else {
            this.$emit('input', entity)
        }
    }

    onChangeMultiple (entities: Entity[]) {
        if (this.idOnly) {
            this.$emit('input', entities.map(this.getOptionValue))
        }
        else {
            this.$emit('input', entities)
        }
    }

    onClear () {
        this.$emit('input', null)
        this.$emit('clear')
    }

    onFilter (filterText: string) {}

    onCreate () { this.$emit('create') }

    onEdit () { this.$emit('edit') }

    ////////////////////////////////////////////////////////////////
    // render
    ////////////////////////////////////////////////////////////////

    render (h: CreateElement) {
        if (!this.multiple) {
            return h(
                UiDropdown,
                {
                    props: {
                        options: this.options,
                        getOptionValue: this.getOptionValue,
                        getOptionFilterKey: this.getOptionFilterKey,
            
                        selectedOption: this.valueToBind,
                        
                        readOnly: this.readOnly,
                        disabled: this.disabled,
                        filterable: this.filterable,
                        editable: this.editable,
                        creatable: this.creatable,
                        async: this.async,
                        highlighted: this.highlighted,
            
                        label: this.label,

                        ...this.$attrs,
                    },
                    on: {
                        'select': this.onSelectOne,
                        'clear': this.onClear,
                        'filter': this.onFilter,
                        'create': this.onCreate,
                        'edit': this.onEdit,
                    },
                    scopedSlots: this.scopedSlotsToUse,
                },
            )
        }
        else {
            return h(
                UiMultiSelect,
                {
                    props: {
                        options: this.options,
                        getOptionValue: this.getOptionValue,
                        getOptionTitle: this.getOptionTitle,
                        getOptionColor: this.getOptionColor,
                        readOnly: this.readOnly,
                        tooltip: this.tooltip,
                        highlighted: this.highlighted,
    
                        selectedOptions: this.valueToBind || [],
    
                        label: this.label,

                        ...this.$attrs,
                    },
                    on: {
                        'change': this.onChangeMultiple,
                    },
                    scopedSlots: this.scopedSlots,
                },
            )
        }
    }
    
}

@Component
// @ts-ignore
export default class InputEntityMixin<Entity extends T.Entity> extends AbstractInputEntityMixin<Entity> {}
